import { useState } from 'react'
import { Button, Card, Text } from 'src/components/ui'
import { FetchModel } from 'src/services'
import { NOT_RUNNING, valueWithUnit } from 'models/model/model.utils'
import { isDefined } from 'src/types'
import {
  ForecastEvaluationBox,
  ImprovementEvaluationBox,
  VariationEvaluationBox,
} from './components'

type Props = {
  model: FetchModel
}

export function ModelEvaluation({ model }: Props): JSX.Element {
  const [showEvaluation, setShowEvaluation] = useState(
    model.state === NOT_RUNNING,
  )

  if (!model.activeTrainedModel) {
    return <></>
  }

  const hasVariationData =
    !!model.activeTrainedModel.targetStdHealth &&
    isDefined(model.activeTrainedModel.targetStd)

  const hasForecastData =
    !!model.activeTrainedModel.valRSquareHealth &&
    isDefined(model.activeTrainedModel.valRSquare)

  const hasPotentialImprovement =
    !!model.activeTrainedModel.targetMaeReductionHealth &&
    isDefined(model.activeTrainedModel.targetMaeReduction)

  return (
    <Card className="col-span-2">
      <div className="flex items-center gap-s">
        <Text bold variant="title">
          Model Evaluation
        </Text>
        {model.state !== NOT_RUNNING && (
          <Button
            title={showEvaluation ? 'Hide' : 'Show'}
            variant="tertiary"
            onClick={() => setShowEvaluation(!showEvaluation)}
          />
        )}
      </div>
      {showEvaluation && (
        <div className="mt-s flex max-w-[1100px] flex-wrap gap-m">
          {hasVariationData && (
            <VariationEvaluationBox
              valueText={
                model.activeTrainedModel?.targetStd
                  ? `+/- ${valueWithUnit(
                      model.activeTrainedModel?.targetStd,
                      model.tag.engUnit,
                    )}`
                  : undefined
              }
              health={model.activeTrainedModel?.targetStdHealth ?? 'YELLOW'}
            />
          )}
          {hasForecastData && (
            <ForecastEvaluationBox
              valueText={
                model.activeTrainedModel?.valRSquare
                  ? `${Math.max(
                      0,
                      model.activeTrainedModel.valRSquare * 100,
                    ).toFixed(2)}%`
                  : undefined
              }
              health={model.activeTrainedModel.valRSquareHealth ?? 'YELLOW'}
            />
          )}
          {hasPotentialImprovement && (
            <ImprovementEvaluationBox
              valueText={
                model.activeTrainedModel.targetMaeReduction
                  ? valueWithUnit(
                      model.activeTrainedModel.targetMaeReduction,
                      model.tag.engUnit,
                    )
                  : undefined
              }
              health={
                model.activeTrainedModel.targetMaeReductionHealth ?? 'YELLOW'
              }
            />
          )}
        </div>
      )}
    </Card>
  )
}
